import { AriaLinkOptions, useLink } from '@react-aria/link';
import React, { useRef } from 'react';
import { useFocusRing } from "@react-aria/focus";
import { useHover } from "@react-aria/interactions";
import clsx from "clsx";
import {useAuth} from "../security/AuthProvider";

interface LinkProps extends AriaLinkOptions {
    children?: React.ReactNode;
    href: string;
    target?: string;
    rel?: string;
    variant?: 'default' | 'inherit' | 'primary' | 'secondary';
    underline?: 'none' | 'hover' | 'always';
    className?: string;
    requiredPermissions?: string[];
}

export const Link = React.forwardRef<HTMLAnchorElement, LinkProps>((props, forwardedRef) => {
    const {
        children,
        href,
        target,
        rel,
        variant = 'default',
        underline = 'hover',
        className,
        requiredPermissions
    } = props;

    const { hasAnyAuthority } = useAuth();

    const internalRef = useRef<HTMLAnchorElement | null>(null);

    // Use useImperativeHandle to sync the forwarded ref with our internal ref
    React.useImperativeHandle(forwardedRef, () => internalRef.current!);

    const { linkProps, isPressed } = useLink(props, internalRef);
    const { isFocused, isFocusVisible, focusProps } = useFocusRing();
    const { hoverProps, isHovered } = useHover({});

    // Build class names based on variant and state
    const linkClasses = clsx(
        // Base styles
        'link-base',
        'cursor-pointer',
        'transition-colors',
        'duration-200',
        'outline-none',

        // Variant styles
        {
            'text-blue-600 hover:text-blue-800': variant === 'default' || variant === 'primary',
            'text-inherit': variant === 'inherit',
            'text-gray-600 hover:text-gray-800': variant === 'secondary',
        },

        // Underline styles
        {
            'no-underline': underline === 'none',
            'no-underline hover:underline': underline === 'hover',
            'underline': underline === 'always',
        },

        // State styles
        {
            'opacity-75': isPressed,
        },

        // Focus styles
        {
            'ring-2 ring-blue-500 ring-offset-2 rounded-sm': isFocusVisible,
        },

        className
    );

    if (requiredPermissions && requiredPermissions.length > 0 && !hasAnyAuthority(requiredPermissions)) {
        return (
            <span ref={internalRef}>
                {children}
            </span>
        );
    }

    return (
        <a
            {...linkProps}
            {...hoverProps}
            {...focusProps}
            ref={internalRef}
            href={href}
            target={target}
            rel={rel}
            className={linkClasses}
        >
            {children}
        </a>
    );
});